import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import {
    linkGoogleAccount,
    listLinkedProviders,
    signInWithGoogle,
    useAuth,
    useAuthActions,
    useUser,
} from "@/lib/core/auth-protocol";
import { seoMeta } from "@/lib/ui/seo";
import { authRedirectSearch } from "./auth-redirect";
import { AuthPage } from "./auth-page";
import { loginSecurityView } from "./login-security";
import { LoginSecuritySurface } from "./login-security-surface";
import { SettingsSurface } from "./settings-surface";

export function SignInRoute() {
    const { redirect_url: redirectUrl, error } = useSearch({
        from: "/sign-in",
    });
    return (
        <AuthPage mode="sign-in" redirectUrl={redirectUrl} oauthError={error} />
    );
}

export function SignUpRoute() {
    const { redirect_url: redirectUrl, error } = useSearch({
        from: "/sign-up",
    });
    return (
        <AuthPage mode="sign-up" redirectUrl={redirectUrl} oauthError={error} />
    );
}

export function SettingsIndex() {
    const auth = useAuthActions();
    const { isSignedIn } = useAuth();
    return (
        <SettingsSurface
            onSignOut={() => void auth.signOut({ redirectUrl: "/find" })}
            isSignedIn={isSignedIn}
            showHeader
        />
    );
}

export function AccountSettings() {
    const { isLoaded, isSignedIn } = useAuth();
    const { user } = useUser();
    const auth = useAuthActions();
    const accountAction = useMutation({
        mutationFn: (action: () => Promise<void>) => action(),
    });
    const linkedProviders = useQuery({
        queryKey: ["auth", "linked-providers", user?.id ?? null],
        queryFn: listLinkedProviders,
        enabled: isLoaded && isSignedIn,
        staleTime: 60_000,
    });
    const view = loginSecurityView({
        isLoaded,
        isSignedIn,
        user,
        linkedProviders,
    });
    return (
        <LoginSecuritySurface
            view={view}
            pending={accountAction.isPending}
            error={
                accountAction.isError
                    ? "That didn't complete. Please try again."
                    : null
            }
            onRetryLinkedProviders={() => void linkedProviders.refetch()}
            onSignIn={() =>
                accountAction.mutate(() =>
                    signInWithGoogle("/settings/account"),
                )
            }
            onConnectGoogle={() =>
                accountAction.mutate(() =>
                    linkGoogleAccount("/settings/account"),
                )
            }
            onSignOut={() =>
                accountAction.mutate(() =>
                    auth.signOut({ redirectUrl: "/find" }),
                )
            }
            showHeader
        />
    );
}

export const signInRoute = {
    validateSearch: authRedirectSearch,
    ssr: false as const,
    head: () => ({
        ...seoMeta({
            title: "Sign in — Wags",
            description: "Sign in to your Wags account.",
            url: "/sign-in",
        }),
    }),
};

export const signUpRoute = {
    validateSearch: authRedirectSearch,
    ssr: false as const,
    head: () => ({
        ...seoMeta({
            title: "Create your account — Wags",
            description: "Create a Wags account.",
            url: "/sign-up",
        }),
    }),
};

export const settingsRoute = {
    ssr: false as const,
};

export const settingsIndexRoute = {
    head: () => ({
        ...seoMeta({
            title: "Settings — Wags",
            description: "Manage your Wags settings.",
            url: "/settings",
        }),
    }),
};

export const accountSettingsRoute = {
    ssr: false as const,
    head: () => ({
        ...seoMeta({
            title: "Login & Security — Wags",
            description: "Manage your Wags account and sign-in security.",
            url: "/settings/account",
        }),
    }),
};
